import { createFileRoute } from "@tanstack/react-router";
import { ChannelProvider } from "ably/react";
import ErrorPopup from "@/components/common/Popups/ErrorPopup";
import LoadingDots from "@/components/LoadingDots";
import ContinueOnScannerDeviceScreen from "@/components/peer/ContinueOnScannerDeviceScreen";
import PeerGradingScreen from "@/components/peer/PeerGradingScreen";
import PeerOfferStatusScreen from "@/components/peer/PeerOfferStatusScreen";
import PeerPairingScreen from "@/components/peer/PeerPairingScreen";
import PeerSessionController from "@/components/peer/PeerSessionController";
import { PeerWaitingScreen } from "@/components/peer/PeerWaitingScreen";
import SelfFunctionalAssessments from "@/components/peer/SelfFunctionalAssessments";
import { servicesOrchestratorValues } from "@/config/services";
import useInitialization from "@/hooks/useInitialization";
import { AblyProvider, configureAbly } from "@/lib/ably";
import { PresenceProvider, visionAblyChannelName } from "@/lib/ably/vision";
import logger from "@/lib/logging/logger";
import { peerSearchParamsSchema } from "@/routes/-search-params";
import { useStore } from "@/store";

configureAbly({ logger });

export const Route = createFileRoute("/$locale/$partner/peer")({
	validateSearch: (search) => peerSearchParamsSchema.parse(search),
	component: LocalePeerPage,
});

function LocalePeerPage() {
	const { sessionId, skipPairing } = Route.useSearch();
	const error = useStore((state) => state.error);
	const setError = useStore((state) => state.setError);
	const channelName = visionAblyChannelName(sessionId);
	const { isLoading } = useInitialization({ configOnly: true });

	if (isLoading) {
		return (
			<div className="flex h-[100dvh] items-center justify-center">
				<LoadingDots />
			</div>
		);
	}

	return (
		<AblyProvider
			apiKey={servicesOrchestratorValues.credentials.ablyKey}
			clientId="evaluated-device"
			logger={logger}
		>
			<ChannelProvider channelName={channelName}>
				<PresenceProvider channelName={channelName} />
				<PeerPageContent
					sessionId={sessionId}
					skipPairing={skipPairing}
				/>
				{error !== null && (
					<ErrorPopup
						show={true}
						error={error}
						setError={setError}
					/>
				)}
			</ChannelProvider>
		</AblyProvider>
	);
}

type PeerPageContentProps = {
	sessionId: string;
	skipPairing: boolean;
};

function PeerPageContent({ sessionId, skipPairing }: PeerPageContentProps) {
	const phase = useStore((s) => s.peerSession.phase);

	return (
		<>
			<PeerSessionController
				sessionId={sessionId}
				skipPairing={skipPairing}
			/>
			{phase === "pairing" && <PeerPairingScreen sessionId={sessionId} />}
			{phase === "grading" && <PeerGradingScreen sessionId={sessionId} />}
			{phase === "waiting-for-results" && <PeerWaitingScreen />}
			{phase === "offer-status" && <PeerOfferStatusScreen />}
			{phase === "self-functional-assessments" && <SelfFunctionalAssessments sessionId={sessionId} />}
			{phase === "continue-on-scanner" && <ContinueOnScannerDeviceScreen />}
		</>
	);
}
